/**
 * Mentor Open Slots service — Firestore CRUD for `mentorOpenSlots`.
 *
 * A document is one one-time extra slot a mentor offers for makeup / rescheduled
 * classes:
 *   {
 *     mentorId, mentorEmail,        // owner (email denormalized for rules)
 *     startAt: Timestamp,           // absolute UTC instant
 *     endAt: Timestamp,             // absolute UTC instant
 *     durationMin,
 *     timezone,                     // IANA-labelled tz the slot was created in
 *     status: 'open'|'booked'|'expired'|'cancelled',
 *     bookedClassId,                // class that consumed this slot (or '')
 *     note,
 *     createdAt, updatedAt
 *   }
 *
 * Visible to the owning mentor and to admins (who book slots when rescheduling).
 */
import firestoreService, { where, Timestamp } from './firestore.service';

const COLLECTION = 'mentorOpenSlots';

// Slot lifecycle (kept dependency-free here; mirrored by OPEN_SLOT_STATUS in
// features/scheduling/constants for the UI).
const STATUS = { OPEN: 'open', BOOKED: 'booked', EXPIRED: 'expired', CANCELLED: 'cancelled' };

/** Coerce a Date/ms/Timestamp into a Firestore Timestamp (or null). */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

function sanitize(input = {}) {
  return {
    mentorId: input.mentorId || '',
    mentorEmail: (input.mentorEmail || '').trim().toLowerCase(),
    startAt: toTimestamp(input.startAt),
    endAt: toTimestamp(input.endAt),
    durationMin: Number(input.durationMin) || 60,
    timezone: input.timezone || '',
    status: input.status || STATUS.OPEN,
    bookedClassId: input.bookedClassId || '',
    note: (input.note || '').trim(),
  };
}

/** Chronological by start instant. */
function sortByStart(rows) {
  return [...rows].sort(
    (a, b) => (a.startAt?.toDate?.()?.getTime?.() ?? 0) - (b.startAt?.toDate?.()?.getTime?.() ?? 0)
  );
}

export const mentorOpenSlotsService = {
  /** Realtime subscription to one mentor's slots (any status), oldest first. */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('mentorId', '==', mentorId)],
      (rows) => onData(sortByStart(rows)),
      onError
    );
  },

  async listByMentor(mentorId) {
    const rows = await firestoreService.getCollection(COLLECTION, [where('mentorId', '==', mentorId)]);
    return sortByStart(rows);
  },

  create(data) {
    return firestoreService.addDocument(COLLECTION, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(COLLECTION, id, sanitize(data));
  },

  /** Flip a slot's lifecycle status (e.g. expire / cancel). */
  setStatus(id, status) {
    return firestoreService.updateDocument(COLLECTION, id, { status });
  },

  /** Mark a slot consumed by a rescheduled class. */
  markBooked(id, classId) {
    return firestoreService.updateDocument(COLLECTION, id, {
      status: STATUS.BOOKED,
      bookedClassId: classId || '',
    });
  },

  remove(id) {
    return firestoreService.deleteDocument(COLLECTION, id);
  },
};

export default mentorOpenSlotsService;
